// src/modules/users/UsersPage.jsx
import React, { useEffect, useState } from "react";
import {
  Button,
  Paper,
  Typography,
  Snackbar,
  Backdrop,
  CircularProgress,
  Box,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useMsal } from "@azure/msal-react";
import DeleteIcon from "@mui/icons-material/Delete";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import DataTable from "../components/DataTable";
import AddUserDialog from "../components/AddUserDialog";
import { getUsers, addUser, deleteUser } from "../api/usersApi";
import { useAuth } from "../auth/useAuth";
import { useUser } from "../context/UserContext"; // ✅ you said it's global now

const UsersPage = () => {
  const { accounts } = useMsal();
  const { getAccessToken } = useAuth();
  const user = useUser(); // 👈 gives us role & location

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingIds, setDeletingIds] = useState(new Set());
  const [openDialog, setOpenDialog] = useState(false);
  const [snackbar, setSnackbar] = useState("");

  // ----------------------------
  // Load Users
  // ----------------------------
  const loadUsers = async () => {
    setLoading(true);
    try {
      const token = await getAccessToken();
      const data = await getUsers(token);
      setUsers(data);
    } catch (err) {
      console.error(err);
      setSnackbar("❌ Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  // ----------------------------
  // Add User
  // ----------------------------
  const handleAddUser = async (form) => {
    setSaving(true);
    try {
      const token = await getAccessToken();
      const payload = { ...form, created_by: accounts[0]?.username };
      await addUser(payload, token);
      setSnackbar("✅ User added successfully");
      setOpenDialog(false);
      await loadUsers();
    } catch (err) {
      console.error(err);
      setSnackbar("❌ Failed to add user");
    } finally {
      setSaving(false);
    }
  };

  // ----------------------------
  // Delete User (per row)
  // ----------------------------
  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    setDeletingIds((prev) => new Set(prev).add(userId));

    try {
      const token = await getAccessToken();
      await deleteUser(userId, token);
      setSnackbar("🗑️ User deleted successfully");
      await loadUsers();
    } catch (err) {
      console.error(err);
      setSnackbar("❌ Failed to delete user");
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  // ----------------------------
  // Table Columns
  // ----------------------------
  const columns = [
    { field: "email", headerName: "Email", flex: 1.4 },
    { field: "display_name", headerName: "Name", flex: 1 },
    { field: "role", headerName: "Role", flex: 0.8 },
    { field: "location", headerName: "Location", flex: 0.8 },
    { field: "created_by", headerName: "Created By", flex: 1 },
    { field: "created_at", headerName: "Created At", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.6,
      sortable: false,
      renderCell: (params) => {
        const isDeleting = deletingIds.has(params.row.id);
        const isAdmin = user?.role === "Administrator";

        if (!isAdmin) {
          // 🔒 Non-admins can’t delete
          return (
            <Tooltip title="Only administrators can delete users">
              <span>
                <IconButton size="small" disabled>
                  <LockOutlinedIcon color="disabled" />
                </IconButton>
              </span>
            </Tooltip>
          );
        }

        return (
          <IconButton
            color="error"
            size="small"
            onClick={() => handleDeleteUser(params.row.id)}
            disabled={isDeleting || saving}
          >
            {isDeleting ? (
              <CircularProgress size={20} color="error" />
            ) : (
              <DeleteIcon />
            )}
          </IconButton>
        );
      },
    },
  ];

  const isAdmin = user?.role === "Administrator";

  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: 3,
        height: "calc(100vh - 140px)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        User Management
      </Typography>

      <Box display="flex" justifyContent="space-between" mb={2}>
        <Tooltip title={!isAdmin ? "Only administrators can add users" : ""}>
          <span>
            <Button
              variant="contained"
              onClick={() => setOpenDialog(true)}
              disabled={!isAdmin || saving}
            >
              Add User
            </Button>
          </span>
        </Tooltip>
      </Box>

      <DataTable rows={users} columns={columns} loading={loading} />

      {/* Add dialog visible only to admins */}
      {isAdmin && (
        <AddUserDialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          onSubmit={handleAddUser}
          fetchToken={getAccessToken}
          saving={saving}
        />
      )}

      {/* Snackbar Feedback */}
      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar("")}
        message={snackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />

      {/* Global overlay only for add/save */}
      <Backdrop open={saving} sx={{ color: "#fff", zIndex: 2000 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Paper>
  );
};

export default UsersPage;
